'use client'
import { CardMedia as AscendCardMedia, Box } from '@achieve/ascend'
import { MediaImageStandard, Typography } from 'components/Contentful'
import classNames from 'classnames'

const tailwindStyles = {
  card: {
    style: 'flex flex-row mb-[32px] lg:flex-col lg:mb-0',
    'card-img': 'w-[70px] mr-4 lg:w-full lg:flex lg:flex-row lg:justify-center lg:mr-0',
    'card-info': 'flex-auto flex flex-col lg:text-center lg:mt-6',
  },
  'card-left-aligned': {
    style: 'flex flex-row mb-[32px] lg:block lg:flex-row lg:justify-start lg:mb-0',
    'card-img': 'w-[70px] mr-4 lg:w-full lg:flex lg:flex-row lg:mr-0',
    'card-info': 'flex-auto flex flex-col lg:text-left lg:mt-6',
    'card-text-subtitle': 'lg:mt-2',
  },
  'card-center-aligned': {
    style: 'mb-[32px] lg:block lg:mb-0',
    'card-img':
      'w-full flex items-center justify-center mr-0 lg:w-full lg:flex lg:items-center lg:justify-center lg:mr-0',
    'card-info': 'flex-auto flex flex-col text-center lg:text-center lg:mt-6',
    'card-text-subtitle': 'lg:mt-2',
  },
  'card-divider': {
    style: 'max-lg:flex max-lg:flex-col max-lg:items-center max-lg:mb-[32px]',
    'card-text-title': 'max-lg:text-center',
    'card-info':
      'md:mt-4 md:px-4 md:py-0 max-lg:flex-auto max-lg:flex max-lg:flex-col max-lg:items-center max-lg:pt-4 max-lg:px-8 max-lg:py-0',
    'card-text-subtitle': 'max-lg:text-center max-lg:mt-4',
  },
  breakline: {
    style: 'flex-col items-center mb-0',
    'card-img': 'mr-0',
    'card-info': 'text-center',
    'card-text-title': 'mt-5 lg:mt-0',
  },
}
const tailwindVariantStyles = {
  standard: {
    style: '',
  },
  advice: {
    style:
      '[&_ul]:list-none [&_ul]:p-0 [&_ul]:gap-2.5 [&_ul]:flex [&_ul]:flex-col [&_a]:text-[#213f7e]',
  },
}

function CardValueProp({
  content,
  breakline,
  dividerOnMobile = false,
  pos = 0,
  cardStyle,
  className,
  gridConfig,
  aplStyling,
}) {
  const { cardTitleText, cardText, cardImage, cardJson, eyebrow } = content || {}
  const titleVariant = gridConfig?.titleVariant ?? 'headingMd'
  const titleVariantMobile = gridConfig?.titleVariantMobile ?? 'bodyLg'
  const subTitleVariant = gridConfig?.subTitleVariant ?? 'bodySm'
  const subTitleVariantMobile = gridConfig?.subTitleVariantMobile ?? 'bodySm'
  const cardNumber = cardJson?.cardNumber ?? false
  const titleWeight = gridConfig?.titleWeight ?? 'font-medium!'
  const subTitleWeight = gridConfig?.subTitleWeight ?? 'font-medium!'

  return (
    <Box
      variant="none"
      data-testid="card-container"
      className={classNames([
        tailwindVariantStyles[gridConfig?.variant || 'standard']?.style,
        cardStyle
          ? tailwindStyles[`card-${cardStyle}`]?.style
          : dividerOnMobile
          ? tailwindStyles['card-divider']?.style
          : tailwindStyles['card']?.style,
        breakline && tailwindStyles['breakline']?.style,
        dividerOnMobile &&
          pos !== 0 &&
          'max-lg:pt-9 max-lg:border-t-[#42546b] max-lg:border-t max-lg:border-solid max-lg:border-b-0 max-lg:border-x-0',
        className,
      ])}
      // data-variant={gridConfig?.variant || 'standard'}
    >
      {cardImage && (
        <Box
          className={classNames(
            !cardStyle
              ? tailwindStyles[`card`]?.['card-img']
              : tailwindStyles[`card-${cardStyle}`]?.['card-img'],
            breakline && tailwindStyles['breakline']?.['card-img']
          )}
        >
          <AscendCardMedia data-testid="ascend-card-media">
            <MediaImageStandard
              data-testid="card-media-image"
              layout="fixed"
              width={cardJson?.imagesize ?? 60}
              height={cardJson?.imagesize ?? 60}
              content={cardImage}
            />
          </AscendCardMedia>
        </Box>
      )}
      <Box
        className={classNames(
          !cardStyle
            ? tailwindStyles[`card`]?.['card-info']
            : tailwindStyles[`card-${cardStyle}`]?.['card-info'],
          breakline && tailwindStyles['breakline']?.['card-info']
        )}
        data-testid="card-value-prop-card-info"
      >
        {cardNumber && (
          <p
            className={classNames(
              !cardStyle
                ? tailwindStyles[`card`]?.['card-text-title']
                : tailwindStyles[`card-${cardStyle}`]?.['card-text-title'],
              breakline && tailwindStyles['breakline']?.['card-text-title'],
              `${titleVariantMobile} lg:${titleVariant} font-medium!`
            )}
          >
            {pos + 1}
          </p>
        )}
        {eyebrow && (
          <Typography
            data-testid="card-eyebrow"
            variant="eyebrow"
            content={eyebrow}
            className={'text-xs'}
          />
        )}
        {cardTitleText && (
          <Typography
            data-testid="card-title"
            className={classNames(
              `${aplStyling ? 'leading-[130%]!' : ''}`,
              !cardStyle
                ? tailwindStyles[`card`]?.['card-text-title']
                : tailwindStyles[`card-${cardStyle}`]?.['card-text-title'],
              `${titleVariantMobile} lg:${titleVariant} ${titleWeight}`
            )}
            variant="normal"
            content={cardTitleText}
          />
        )}

        {cardText && (
          <Typography
            data-testid="card-subtitle"
            className={classNames(
              'mt-2',
              `${aplStyling ? 'leading-[170%]!' : ''}`,
              !cardStyle
                ? tailwindStyles[`card`]?.['card-text-subtitle']
                : tailwindStyles[`card-${cardStyle}`]?.['card-text-subtitle'],
              `${subTitleVariantMobile} lg:${subTitleVariant} ${subTitleWeight}`
            )}
            component="p"
            content={cardText}
          />
        )}
      </Box>
    </Box>
  )
}

export { CardValueProp }
